/**
 * GLSL for the "Mosaic GL" experiments compositor (mosaicGlCompositor.ts):
 * one fullscreen draw resamples a single R8/LUMINANCE mosaic raster through
 * a runtime-toggleable kernel — nearest / bilinear / bicubic (Catmull-Rom,
 * 16-tap) — entirely in DATA SPACE on the raw quantized u8 values, never on
 * colorized RGB, then colorizes via a palette LUT texture
 * (mosaicGlPalette.ts). Targets GLSL ES 1.00 (attribute/varying/texture2D/
 * gl_FragColor), same reasoning as fusionShader.ts's own doc comment: one
 * source runs unmodified on both a "webgl" and a "webgl2" context.
 *
 * No dynamic vector/array indexing anywhere (every sampling tap is a
 * separately-named, unrolled block) — the same portability discipline
 * fusionShader.ts's doc comment describes for WebGL1/GLSL ES 1.00 sampler
 * arrays, applied here to texel taps and Catmull-Rom weight lookups too,
 * since it costs nothing at only 16 taps and sidesteps a whole class of
 * driver-specific bug reports.
 *
 * Reuses fusionShader.ts's VERTEX_SHADER_SOURCE and compileShader/
 * compileProgram verbatim (re-exported below) — the vertex stage and GLSL
 * compile plumbing are generic, nothing here needs a second copy.
 */
export { VERTEX_SHADER_SOURCE, compileShader, compileProgram } from "./fusionShader";
import type { SamplingMode, FloorOrder, FloorHardness } from "./mosaicGlCompositor";

export const SAMPLING_MODE_INDEX: Record<SamplingMode, 0 | 1 | 2> = {
  nearest: 0,
  bilinear: 1,
  bicubic: 2,
};

export const FLOOR_ORDER_INDEX: Record<FloorOrder, 0 | 1> = {
  after: 0,
  before: 1,
};

export const FLOOR_HARDNESS_INDEX: Record<FloorHardness, 0 | 1> = {
  feathered: 0,
  hard: 1,
};

/** Catmull-Rom tap offsets relative to `floor(t)`, in `catmullRomWeights1D`
 * (mosaicGlKernel.ts) order — offset -1 is weight index 0, offset 2 is
 * weight index 3. */
const CR_TAP_OFFSETS = [-1, 0, 1, 2] as const;

function crWeightFnName(offset: number): string {
  return `crWeight${offset + 1}`;
}

function bilinearTapBlock(dx: 0 | 1, dy: 0 | 1): string {
  const wx = dx === 0 ? "(1.0 - f.x)" : "f.x";
  const wy = dy === 0 ? "(1.0 - f.y)" : "f.y";
  return `
  {
    vec2 rm = fetchRawMask(i0 + vec2(${dx.toFixed(1)}, ${dy.toFixed(1)}));
    float w = ${wx} * ${wy};
    valSum += w * rm.x * rm.y;
    coverage += w * rm.y;
  }`;
}

function bicubicTapBlock(dx: number, dy: number): string {
  return `
  {
    vec2 rm = fetchRawMask(i0 + vec2(${dx.toFixed(1)}, ${dy.toFixed(1)}));
    float w = ${crWeightFnName(dx)}x * ${crWeightFnName(dy)}y;
    valSum += w * rm.x * rm.y;
    coverage += w * rm.y;
  }`;
}

export function buildMosaicGlFragmentShaderSource(): string {
  const bilinearTaps = ([0, 1] as const).flatMap((dy) => ([0, 1] as const).map((dx) => bilinearTapBlock(dx, dy))).join("\n");
  const bicubicTaps = CR_TAP_OFFSETS.flatMap((dy) => CR_TAP_OFFSETS.map((dx) => bicubicTapBlock(dx, dy))).join("\n");

  return `
precision highp float;
varying vec2 v_uv;

uniform sampler2D u_tex;       // R8/LUMINANCE raw mosaic raster at the LOD-selected IFD (mosaicGlCompositor.ts) — always NEAREST-filtered at the GL level; every resampling mode below is our OWN data-space kernel, never hardware texture filtering
uniform vec2 u_texSize;        // (width, height) of u_tex, in texels
uniform vec4 u_uvXform;        // (scaleX, scaleY, offsetX, offsetY): window UV -> u_tex UV (fusionGeo.ts's stationUvTransform convention)
uniform int u_sampling;        // 0=nearest, 1=bilinear, 2=bicubic (Catmull-Rom, 16-tap) — SAMPLING_MODE_INDEX above
uniform int u_floorOrder;      // 0=after (default), 1=before — FLOOR_ORDER_INDEX above
uniform float u_floor;         // normalized raw-byte display floor (raw/255); 0 = "Precip only" OFF, same convention as fusionCompositor.ts's PRECIP_FLOOR_RAW/NO_FLOOR_RAW
uniform int u_floorHard;       // 0=feathered (smoothstep ramp), 1=hard (binary step) — FLOOR_HARDNESS_INDEX above
uniform float u_featherWidth;  // feathered floor's ramp width, normalized raw-byte units (3 dBZ, mosaicGlCompositor.ts's FEATHER_WIDTH_NORM)
uniform sampler2D u_palette;   // banded (256x1, NEAREST) or continuous (1024x1, LINEAR) LUT — mosaicGlPalette.ts, bound by mosaicGlCompositor.ts's palette toggle
uniform float u_paletteSize;   // texel count of u_palette, for correctly-centered indexing at either LUT size

// Fetches one texel's raw value and its validity as (raw, valid) in [0,1].
// "Valid" always excludes the shared nodata sentinel (raw == 0); with
// floor-BEFORE also in effect (u_floorOrder == 1 and a floor is active), a
// sub-floor texel is excluded from the interpolation INPUT itself, not just
// clipped afterward below — this single branch is the entire behavioral
// difference between floor-before and floor-after; nothing else in this
// shader depends on which order is selected.
vec2 fetchRawMask(vec2 texelCoord) {
  vec2 uv = (texelCoord + 0.5) / u_texSize;
  float raw = texture2D(u_tex, uv).r;
  bool floorBefore = u_floorOrder == 1 && u_floor > 0.0;
  float valid = (raw > 0.0 && (!floorBefore || raw >= u_floor)) ? 1.0 : 0.0;
  return vec2(raw, valid);
}

// Standard uniform Catmull-Rom (tension -0.5) 1D weights, one named function
// per tap (offsets -1, 0, 1, 2 from floor(t)) instead of a vec4-indexed
// form — see this file's top doc comment on why. Must stay numerically
// identical to mosaicGlKernel.ts's catmullRomWeights1D, the pure,
// unit-tested reference these four are hand-derived from.
float crWeight0(float t) { return -0.5 * t * t * t + t * t - 0.5 * t; }
float crWeight1(float t) { return 1.5 * t * t * t - 2.5 * t * t + 1.0; }
float crWeight2(float t) { return -1.5 * t * t * t + 2.0 * t * t + 0.5 * t; }
float crWeight3(float t) { return 0.5 * t * t * t - 0.5 * t * t; }

void main() {
  vec2 texUv = v_uv * u_uvXform.xy + u_uvXform.zw;
  vec2 tc = texUv * u_texSize - 0.5;

  float valSum = 0.0;
  float coverage = 0.0;

  if (u_sampling == 0) {
    vec2 rm = fetchRawMask(floor(tc + 0.5));
    valSum = rm.x * rm.y;
    coverage = rm.y;
  } else if (u_sampling == 1) {
    vec2 i0 = floor(tc);
    vec2 f = fract(tc);
${bilinearTaps}
  } else {
    vec2 i0 = floor(tc);
    vec2 f = fract(tc);
    float crWeight0x = crWeight0(f.x); float crWeight1x = crWeight1(f.x); float crWeight2x = crWeight2(f.x); float crWeight3x = crWeight3(f.x);
    float crWeight0y = crWeight0(f.y); float crWeight1y = crWeight1(f.y); float crWeight2y = crWeight2(f.y); float crWeight3y = crWeight3(f.y);
${bicubicTaps}
  }

  // Ratio, not sum: a nodata/sub-floor neighbor contributes 0 to BOTH
  // valSum and coverage, so it only reduces the weight the average is
  // normalized over — it never drags the value itself toward 0 the way
  // summing un-normalized weighted raw values would. A real return's edge
  // stays at its own true value right up to where coverage drops below the
  // transparency threshold, instead of smearing toward zero. (When u_tex
  // is mosaicGlFieldSmoothing.ts's pre-smoothed field rather than the raw
  // grid, every texel is already >0 by construction — coverage is always
  // ~1.0 there, so this whole ratio/threshold machinery is a harmless no-op
  // rather than a second, conflicting nodata concept stacked on the blur.)
  float value = coverage > 0.0 ? valSum / coverage : 0.0;
  // Coverage validity is always a hard cutoff — "not enough real data to
  // trust this pixel at all" is a different question from "the value is
  // below the display floor", and only the LATTER gets the hard/feathered
  // choice below.
  float coverageAlpha = coverage < 0.4 ? 0.0 : 1.0;

  // Floor-after (default, FloorOrder): the floor clips the INTERPOLATED
  // value post-hoc — the kernel above never saw it. Floor-before
  // (fetchRawMask, above) instead removes sub-floor texels from the
  // kernel's own input, which is what visibly keeps blocky edges (the
  // study's comparison case, not the winner). Re-applying floorAlpha here
  // even when floor-before already masked the inputs is a harmless,
  // strictly-safe reinforcement (value is already >= the floor in all but
  // rounding-edge-case pixels right at a validity boundary), not a second,
  // conflicting cutoff.
  //
  // FloorHardness (u_floorHard) controls HOW that cutoff turns into alpha:
  // "hard" is the original binary step; "feathered" is a smoothstep ramp
  // over a 3 dBZ window (u_featherWidth) — round-3 study spec, since a hard
  // cutoff on top of a SMOOTHED field reintroduces exactly the stairstep
  // artifact the smoothing was meant to remove.
  float floorAlpha = 1.0;
  if (u_floor > 0.0) {
    if (u_floorHard == 1) {
      floorAlpha = value >= u_floor ? 1.0 : 0.0;
    } else {
      floorAlpha = smoothstep(u_floor, u_floor + u_featherWidth, value);
    }
  }

  float idx = clamp(value, 0.0, 1.0);
  vec2 paletteUv = vec2((idx * (u_paletteSize - 1.0) + 0.5) / u_paletteSize, 0.5);
  vec4 paletteColor = texture2D(u_palette, paletteUv);

  // Standard premultiplied-alpha output: composites cleanly over the
  // basemap without dark fringing at the now-common fractional alpha
  // values the feathered floor introduces (the old binary transparent/
  // opaque cutoff never differed between straight and premultiplied, so
  // this was never visible before). mosaicGlCompositor.ts creates its GL
  // context with premultipliedAlpha:true to match.
  float alpha = coverageAlpha * floorAlpha * paletteColor.a;
  gl_FragColor = vec4(paletteColor.rgb * alpha, alpha);
}
`;
}
